import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import CompanyRegistration from './pages/CompanyRegistration';
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CompanyDashboard from './pages/CompanyDashboard';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import InboxPage from './pages/InboxPage';
import RegistrationSuccess from './pages/RegistrationSuccess';
import DemoRequest from './pages/DemoRequest';
import CompanyInformation from './components/CompanyInformation';
import AddTagPageWrapper from './components/AddTagPageWrapper';
import AddUserPageWrapper from './components/AddUserPageWrapper';
import TeamOrganization from './components/TeamOrganization';
import Profile from './components/Profile';
import PageLayout from './components/ui/PageLayout';
import ClientList from './ClientList.js';
import ClientDetails from './ClientDetails.js';
import SettingsPanel from './components/SettingsPanel';
import RegistrationPage from './components/RegistrationPage';
import FolderFileExplorer from './components/FolderFileExplorer';
import OrganizerDetail from './components/OrganizerDetail';
import OrganizerSecureOrganizer from './components/OrganizerSecureOrganizer';
// Billing Components
import BillingDashboard from './components/BillingDashboard';
import BillingInvoices from './components/BillingInvoices';
import BillingPayments from './components/BillingPayments';
import BillingCredits from './components/BillingCredits';
import CreateCredit from './components/CreateCredit';
import BillingExpenses from './components/BillingExpenses';
import BillingStatements from './components/BillingStatements';
import BillingReports from './components/BillingReports';
import AgedReceivablesReport from './components/AgedReceivablesReport';
import CreateInvoice from './components/CreateInvoice';
import BillingLayout from './components/BillingLayout';
import ActivityLog from './components/ActivityLog';
import WorkflowManagement from './pages/WorkflowManagement';
import WorkflowKanbanBoard from './components/WorkflowKanbanBoard';
import WorkflowAnalytics from './components/WorkflowAnalytics';
import WorkflowTemplatesPage from './components/WorkflowTemplatesPage';
import TaxOrganizersPage from './pages/TaxOrganizersPage';
import PlatformBilling from './pages/PlatformBilling';
import TasksPage from './pages/TasksPage';
import EventsPage from './pages/EventsPage';
import { AuthProvider } from './components/AuthContext';
import { EmailSuggestionsProvider } from './contexts/EmailSuggestionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import NotFoundPage from './pages/NotFoundPage';
import ClientRequestsPage from './pages/ClientRequests';
import PricingPage from './pages/PricingPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import ReportsLayout from './components/ReportsLayout';
import ProfitabilityReport from './pages/ProfitabilityReport';
import RevenueForecast from './pages/RevenueForecast';
import StaffUtilization from './pages/StaffUtilization';
import TimeTracking from './pages/TimeTracking';
import ClientActivityReport from './pages/reports/ClientActivityReport';
import InvoiceSummaryReport from './pages/reports/InvoiceSummaryReport';
import GracePeriodBanner from './components/GracePeriodBanner';
import TrialCountdownBanner from './components/TrialCountdownBanner';
import CommandPalette from './components/CommandPalette';
import PlatformAdmin from './pages/PlatformAdmin';
import SuggestFeature from './pages/SuggestFeature';
import api from './api';
import './App.css';

function CreateInvoicePage() {
  const navigate = useNavigate();
  return <CreateInvoice onBack={() => navigate('/billing/invoices')} />;
}

function App() {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('jwtToken');
    if (token) {
      api.get('/api/platform/subscription/status/')
        .then(res => {
          setSubscriptionStatus(res.data?.status);
          setTrialEnd(res.data?.trial_end);
        })
        .catch(() => {});
    }
  }, []);

  return (
    <ErrorBoundary>
    <AuthProvider>
    <Router>
    <EmailSuggestionsProvider>
        <div className="App">
          <GracePeriodBanner subscriptionStatus={subscriptionStatus} />
          <TrialCountdownBanner subscriptionStatus={subscriptionStatus} trialEnd={trialEnd} />
      <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<CompanyRegistration />} />
            <Route path="/user-register" element={<RegistrationPage />} />
        <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/logout" element={<Navigate to="/login" replace />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing/success" element={<CheckoutSuccessPage />} />
            <Route path="/add-tag" element={<ProtectedRoute><AddTagPageWrapper /></ProtectedRoute>} />
            <Route path="/add-user" element={<ProtectedRoute><AddUserPageWrapper /></ProtectedRoute>} />
            
            {/* Dashboard and Settings Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><CompanyDashboard /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><AdminNotificationsPage /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
            <Route path="/time-entries" element={<ProtectedRoute><TimeTracking /></ProtectedRoute>} />
            <Route path="/settings/*" element={<ProtectedRoute><SettingsPanel /></ProtectedRoute>} />
            
            {/* File Management Routes */}
            <Route path="/files/clients/root" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/clients/:clientId" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/clients/:clientId/folders/:folderId/contents" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/internal/root" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/internal/folders/:folderId/contents" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/personal/root" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/personal/folders/:folderId/contents" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            <Route path="/files/recent/root" element={<ProtectedRoute><FolderFileExplorer /></ProtectedRoute>} />
            
            {/* Billing Routes - nested under BillingLayout */}
            <Route path="/billing" element={<ProtectedRoute><BillingLayout /></ProtectedRoute>}>
              <Route index element={<PlatformBilling />} />
              <Route path="dashboard" element={<BillingDashboard />} />
              <Route path="invoices" element={<BillingInvoices />} />
              <Route path="invoices/create" element={<CreateInvoicePage />} />
              <Route path="payments" element={<BillingPayments />} />
              <Route path="credits" element={<BillingCredits />} />
              <Route path="credits/create" element={<CreateCredit />} />
              <Route path="expenses" element={<BillingExpenses />} />
              <Route path="statements" element={<BillingStatements />} />
              <Route path="reports" element={<BillingReports />} />
              <Route path="reports/aged-receivables-summary" element={<AgedReceivablesReport />} />
              <Route path="reports/aged-receivables-detail" element={<AgedReceivablesReport initialTab={1} />} />
            </Route>
            
            {/* Activity Log Route */}
            <Route path="/activity-log" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
            
            {/* Workflow Management Routes — static paths before /workflows/:id */}
            <Route path="/workflows" element={<ProtectedRoute><WorkflowManagement /></ProtectedRoute>} />
            <Route path="/workflows/templates" element={<ProtectedRoute><PageLayout><WorkflowTemplatesPage /></PageLayout></ProtectedRoute>} />
            <Route path="/workflows/kanban" element={<ProtectedRoute><WorkflowKanbanBoard /></ProtectedRoute>} />
            <Route path="/workflows/analytics" element={<ProtectedRoute><WorkflowAnalytics /></ProtectedRoute>} />
            <Route path="/workflows/:id" element={<Navigate to="/workflows" replace />} />
            
            {/* Tax Organizers Route */}
            <Route path="/tax-organizers" element={<ProtectedRoute><TaxOrganizersPage /></ProtectedRoute>} />
            
            {/* Other Routes */}
            <Route path="/team-organization" element={<ProtectedRoute><TeamOrganization /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><PageLayout><Profile /></PageLayout></ProtectedRoute>} />
            <Route path="/client_list" element={<ProtectedRoute><ClientList /></ProtectedRoute>} />
            <Route path="/clients" element={<Navigate to="/client_list" replace />} />
            <Route path="/clients/new" element={<Navigate to="/client_list" replace />} />
            <Route path="/client/:id/requests" element={<ProtectedRoute><ClientDetails /></ProtectedRoute>} />
            <Route path="/client/:id" element={<ProtectedRoute><ClientDetails /></ProtectedRoute>} />
            <Route path="/client-requests/:requestId" element={<ProtectedRoute><ClientRequestsPage /></ProtectedRoute>} />
            <Route path="/client-requests" element={<ProtectedRoute><ClientRequestsPage /></ProtectedRoute>} />
            <Route path="/organizer/:id" element={<ProtectedRoute><OrganizerDetail /></ProtectedRoute>} />
            <Route path="/organizer-secure/:token" element={<OrganizerSecureOrganizer />} />
            
            {/* Settings Routes - These are now handled by SettingsPanel with nested routing */}
            <Route path="/company/information/" element={<ProtectedRoute><CompanyInformation /></ProtectedRoute>} />
            
            <Route path="/demo" element={<DemoRequest />} />

            {/* Reports - unified layout with sidebar nav */}
            <Route path="/reports" element={<ProtectedRoute><ReportsLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/reports/profitability" replace />} />
              <Route path="profitability" element={<ProfitabilityReport />} />
              <Route path="forecast" element={<RevenueForecast />} />
              <Route path="staff" element={<StaffUtilization />} />
              <Route path="client-activity" element={<ClientActivityReport />} />
              <Route path="invoice-summary" element={<InvoiceSummaryReport />} />
            </Route>

            {/* Suggest Feature */}
            <Route path="/suggest-feature" element={<ProtectedRoute><SuggestFeature /></ProtectedRoute>} />

            {/* Platform Super Admin (superuser only) */}
            <Route path="/platform-admin" element={<ProtectedRoute><PlatformAdmin /></ProtectedRoute>} />

            {/* 404 Catch-All */}
            <Route path="*" element={<ProtectedRoute><NotFoundPage /></ProtectedRoute>} />
                </Routes>
          <CommandPalette />
        </div>
    </EmailSuggestionsProvider>
      </Router>
            </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;